// web-commands.ts — the PER-APP half of the web shape: maps solo_focus_log's Tauri data commands
// (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore. Plain CRUD
// (+ the key-value settings table) is a few lines each. The derived command (weekly_signals) is
// computed on-device in Rust — on web it returns a valid-but-empty shell so the recap UI never
// crashes; a faithful web build ports that signal logic to JS (known per-app wrinkle, see web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import type { FocusSession } from "./store";
import { weeklySignalsWeb } from "./signals-web";
import { browserSearch, browserFetch } from "./web-search-browser";

const SESSIONS = "focus_session";
const SETTINGS = "settings";
const PLANS = "focus_plan";
const PLANNED = "planned_session";
const GOALS = "goal";
const DEFAULT_DURATION_MIN = 25;

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    list_sessions: ({ limit }) => {
      const rows = db.all(SESSIONS).sort((a, b) => String(b.started_at).localeCompare(String(a.started_at)));
      return typeof limit === "number" ? rows.slice(0, limit) : rows;
    },
    // Raw slice scoped to the last N days (a plain filter, not a signal — returns real rows).
    recent_sessions: ({ days }) => {
      const cutoff = new Date(Date.now() - Number(days ?? 7) * 86400000).toISOString();
      return db
        .all(SESSIONS)
        .filter((r) => String(r.started_at) >= cutoff)
        .sort((a, b) => String(b.started_at).localeCompare(String(a.started_at)));
    },
    create_session: ({ input }) => {
      const inp = (input ?? {}) as Record<string, unknown>;
      const row = {
        id: uuid(),
        duration_min: (inp.duration_min as number) ?? 0,
        started_at: (inp.started_at as string) ?? nowIso(),
        completed_at: inp.completed_at ?? null,
        reflection: inp.reflection ?? null,
        created_at: nowIso(),
        goal_id: inp.goal_id ?? null,
        // Defaulted here because native SQLite defaults the column to '' — without it a
        // web-created session would render `undefined` where the phone renders nothing.
        photo_ref: (inp.photo_ref as string) ?? "",
      };
      return db.insert(SESSIONS, row);
    },
    delete_session: ({ id }) => {
      db.remove(SESSIONS, String(id));
      return null;
    },
    set_session_photo: ({ id, photo_ref }) => {
      db.update(SESSIONS, String(id), { photo_ref: String(photo_ref ?? "") });
      return null;
    },
    // Settings modeled as a key-value table (id = the setting key).
    get_settings: () => {
      const row = db.get(SETTINGS, "default_duration_min") as { value?: unknown } | null;
      const v = row?.value;
      return { default_duration_min: v === undefined || v === null ? DEFAULT_DURATION_MIN : Number(v) };
    },
    set_default_duration: ({ minutes }) => {
      const value = Number(minutes);
      if (db.get(SETTINGS, "default_duration_min")) db.update(SETTINGS, "default_duration_min", { value });
      else db.insert(SETTINGS, { id: "default_duration_min", value });
      return null;
    },
    // Derived aggregate. Ported faithfully to JS in ./signals-web.ts — this used to return a
    // valid-but-EMPTY shell, so on web the SignalStrip never rendered and the billed weekly
    // summary was handed a week with no sessions in it (ledger row 2c). The Rust in db.rs
    // remains the authority; signals-web.ts mirrors it rule for rule.
    weekly_signals: ({ days }) =>
      weeklySignalsWeb(db.all(SESSIONS) as unknown as FocusSession[], Number(days ?? 7)),

    // ── Focus Plan — real port, not a stub (ledger row 2c: an empty shell here would silently
    // hide the whole feature on web AND bill the draft call against data the AI never sees). ──
    active_plan: () => {
      const rows = db.all(PLANS).filter((p) => p.status === "active");
      rows.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      return rows[0] ?? null;
    },
    list_planned_sessions: ({ plan_id }) =>
      db.all(PLANNED).filter((s) => s.plan_id === plan_id).sort((a, b) => Number(a.weekday) - Number(b.weekday)),
    next_planned_session: () => {
      const active = db.all(PLANS).filter((p) => p.status === "active").sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
      if (!active) return null;
      const todayWd = (new Date().getDay() + 6) % 7; // JS getDay is 0=Sun -> convert to 0=Mon
      let best: { forward: number; row: Record<string, unknown> } | null = null;
      for (const s of db.all(PLANNED).filter((s) => s.plan_id === active.id)) {
        if (s.completed_session_id) continue;
        const forward = (((Number(s.weekday) - todayWd) % 7) + 7) % 7;
        if (!best || forward < best.forward) best = { forward, row: s };
      }
      return best?.row ?? null;
    },
    create_focus_plan: ({ input }) => {
      const inp = (input ?? {}) as { name?: string; sessions?: Record<string, unknown>[] };
      for (const p of db.all(PLANS).filter((p) => p.status === "active")) db.update(PLANS, String(p.id), { status: "abandoned" });
      const planId = uuid();
      const now = nowIso();
      const plan = { id: planId, name: inp.name ?? "Focus Plan", status: "active", created_at: now };
      db.insert(PLANS, plan);
      for (const s of inp.sessions ?? []) {
        db.insert(PLANNED, {
          id: uuid(), plan_id: planId,
          weekday: s.weekday, band: s.band, duration_min: s.duration_min,
          label: s.label, reason: s.reason ?? "", completed_session_id: null,
        });
      }
      return plan;
    },
    abandon_active_plan: () => {
      for (const p of db.all(PLANS).filter((p) => p.status === "active")) db.update(PLANS, String(p.id), { status: "abandoned" });
      return null;
    },
    complete_planned_session: ({ planned_id, session_id }) => {
      db.update(PLANNED, String(planned_id), { completed_session_id: session_id });
      return null;
    },

    // ── Goals — real port, not a stub (same ledger-row-2c reasoning as Focus Plan above). ──
    create_goal: ({ input }) => {
      const inp = (input ?? {}) as { title?: string };
      const title = (inp.title ?? "").trim();
      if (!title) throw new Error("Goal needs a title");
      const row = { id: uuid(), title, created_at: nowIso(), archived_at: null };
      return db.insert(GOALS, row);
    },
    list_goals: ({ include_archived }) => {
      const rows = db.all(GOALS).filter((g) => include_archived || !g.archived_at);
      return rows.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
    },
    archive_goal: ({ goal_id }) => {
      db.update(GOALS, String(goal_id), { archived_at: nowIso() });
      return null;
    },
    delete_goal: ({ goal_id }) => {
      for (const s of db.all(SESSIONS).filter((s) => s.goal_id === goal_id)) db.update(SESSIONS, String(s.id), { goal_id: null });
      db.remove(GOALS, String(goal_id));
      return null;
    },
    goal_stats: ({ goal_id }) => {
      const rows = db.all(SESSIONS).filter((s) => s.goal_id === goal_id).sort((a, b) => String(b.started_at).localeCompare(String(a.started_at)));
      const now = Date.now();
      const curStart = new Date(now - 7 * 86400000).toISOString();
      const priorStart = new Date(now - 14 * 86400000).toISOString();
      const thisWeek = rows.filter((s) => String(s.started_at) >= curStart).reduce((sum, s) => sum + Number(s.duration_min), 0);
      const priorWeek = rows.filter((s) => String(s.started_at) >= priorStart && String(s.started_at) < curStart).reduce((sum, s) => sum + Number(s.duration_min), 0);
      return {
        goal_id,
        sessions_total: rows.length,
        total_minutes: rows.reduce((sum, s) => sum + Number(s.duration_min), 0),
        last_session_at: rows[0]?.started_at ?? null,
        this_week_minutes: thisWeek,
        prior_week_minutes: priorWeek,
      };
    },
  };
}
